import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line } from 'recharts';

interface CedularRentData {
  [key: string]: any;
}

interface CedularRentViewProps {
  data: CedularRentData[];
  title: string;
}

const formatYAxis = (tickItem: number) => {
  if (tickItem >= 1000000000) {
    return `${(tickItem / 1000000000).toFixed(1)}B`;
  }
  if (tickItem >= 1000000) {
    return `${(tickItem / 1000000).toFixed(1)}M`;
  }
  if (tickItem >= 1000) {
    return `${(tickItem / 1000).toFixed(1)}K`;
  }
  return tickItem.toString();
};

const CedularRentView: React.FC<CedularRentViewProps> = ({ data, title }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography>No hay datos disponibles para {title}.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" gutterBottom>Análisis de Renta Cedular</Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="sub-vistas de renta cedular">
          <Tab label="Tabla Completa" />
          <Tab label="Análisis Resumido" />
          <Tab label="Evolución Gráfica" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Tabla Completa de Rentas por Año</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Año</TableCell>
                  <TableCell>Renta Capital</TableCell>
                  <TableCell>Ingresos Ganancias Ocasional</TableCell>
                  <TableCell>Renta no Laborales</TableCell>
                  <TableCell>Renta Trabajo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.Anio}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Renta_Capital)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Ingresos_ganancias_ocasional)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Renta_no_laborales)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Renta_trabajo)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Tabla Resumida con Análisis</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Año</TableCell>
                  <TableCell>Renta Capital</TableCell>
                  <TableCell>Ingresos Ganancias Ocasional</TableCell>
                  <TableCell>Renta no Laborales</TableCell>
                  <TableCell>Renta Trabajo</TableCell>
                  <TableCell>Renta Cedular</TableCell>
                  <TableCell>Análisis %</TableCell>
                  <TableCell>Análisis $</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.Anio}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Renta_Capital)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Ingresos_ganancias_ocasional)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Renta_no_laborales)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Renta_trabajo)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Renta_cedular)}</TableCell>
                    <TableCell>{row['Analisis %'].toFixed(2)}%</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row['Analisis $'])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Evolución de la Renta Cedular</Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Anio" />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip formatter={(value: number) => new Intl.NumberFormat('es-CO').format(value)} />
              <Legend />
              <Bar dataKey="Renta_cedular" fill={theme.palette.primary.main} name="Renta Cedular" />
              <Line type="monotone" dataKey="Renta_cedular" stroke={theme.palette.secondary.main} name="Tendencia" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
};

export default CedularRentView;